import { useEffect } from "react"
import { useRecoilCallback, useRecoilValue } from "recoil"
import { useEventReview, useEventReviewForm } from "../../../../../Providers/EventReviewProvider"
import { navigationCursorAtom } from "../Atoms/NavigationCursor"
import { selectedLayoutAtom, selectedLayoutGroupAtom } from "../Atoms/Layout"
import { isAnnotationPlaythroughController, linkedWindowsTimelineControllerAtom } from "../Atoms/Timeline"
import { jumpAllWindows, allWindowsDispatch } from "../Atoms/Visualizations"
import { LinkedWindowEventType, WindowClickedEvent } from "../Types/LinkedWindowInfo"
import { VisualizationComponent } from "../VisualizationComponent"

const ALLOWED_CLICK_COMPONENTS = new Set<string>([
	VisualizationComponent.TIME_SERIES_GROUP,
	VisualizationComponent.EEG_MONTAGE,
])

export const useEventReviewNavigationCursorClickHandler = () => {
	const eventReview = useEventReview()
	const eventReviewForm = useEventReviewForm()
	const timelineController = useRecoilValue(linkedWindowsTimelineControllerAtom)

	const handleClick = useRecoilCallback(
		({ snapshot, set }) =>
			(event: WindowClickedEvent) => {
				const lastHoveredDate = event.lastHoveredDate
				if (!lastHoveredDate) {
					return
				}

				const selectedLayout = snapshot.getLoadable(selectedLayoutAtom).getValue()
				const selectedLayoutGroup = snapshot.getLoadable(selectedLayoutGroupAtom).getValue()
				if (!selectedLayout) {
					return
				}

				const areaConfig = selectedLayout.areas.find(area => area.area === event.controller)
				if (!areaConfig || !ALLOWED_CLICK_COMPONENTS.has(areaConfig.componentId)) {
					return
				}

				set(navigationCursorAtom, previous => ({
					...previous,
					isVisible: true,
					layoutGroupId: selectedLayoutGroup?.id ?? null,
					cursorTime: null,
				}))
					jumpAllWindows(lastHoveredDate, LinkedWindowEventType.JUMP_MIDDLE, { autoScale: false, controllerIsLinked: true })
			},
		[],
	)

	useEffect(() => {
		const listener = (event: WindowClickedEvent) => {
			if (!eventReview.isActive || isAnnotationPlaythroughController(timelineController) || eventReviewForm.isSetDateTimeActive) {
				return
			}

			handleClick(event)
		}

		allWindowsDispatch.on("click.eventReviewNavigationCursor", listener)

		return () => {
			allWindowsDispatch.on("click.eventReviewNavigationCursor", null)
		}
	}, [eventReview.isActive, eventReviewForm.isSetDateTimeActive, handleClick, timelineController])
}
